import type { MatrixClient, Room } from 'matrix-js-sdk'
import type { IMessage, IMessageAttachment } from '@/modules/chat-matrix-fast/types/IChat'
import { findViewAvatarByMatrixUserId } from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import { resolvePeerAvatarFromBackend } from '@/modules/chat-matrix-fast/services/employeeLookup'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'
import { rewriteMatrixHomeserverUrl } from '@/modules/chat-matrix-fast/config'
import {
  isMatrixMediaHttpUrl,
  toAuthenticatedMatrixMediaUrl,
} from '@/modules/chat-matrix-fast/services/matrixMediaUrl'

export interface MatrixSenderProfile {
  displayName: string
  avatarUrl: string | null
}

const profileCache = new Map<string, MatrixSenderProfile>()
const profilePending = new Map<string, Promise<MatrixSenderProfile>>()
const backendAvatarMissAt = new Map<string, number>()
const BACKEND_AVATAR_MISS_TTL_MS = 10 * 60_000

function isBackendAvatarRecentlyMissing(mxid: string): boolean {
  const at = backendAvatarMissAt.get(mxid)
  return Boolean(at) && Date.now() - (at as number) < BACKEND_AVATAR_MISS_TTL_MS
}

async function resolveBackendAvatarCached(mxid: string): Promise<string | null> {
  if (isBackendAvatarRecentlyMissing(mxid)) {
    return null
  }

  const avatar = await resolvePeerAvatarFromBackend(mxid)
  if (avatar) {
    backendAvatarMissAt.delete(mxid)
  } else {
    backendAvatarMissAt.set(mxid, Date.now())
  }
  return avatar
}

function mxcToHttp(matrixClient: MatrixClient, mxcUrl: string | null | undefined): string | null {
  if (!mxcUrl) {
    return null
  }

  return toAuthenticatedMatrixMediaUrl(matrixClient, mxcUrl, 96, 96, 'crop')
}

function rewriteAttachmentMediaUrl(url: string): string {
  if (url.includes('/_matrix/media/v3/')) {
    return rewriteMatrixHomeserverUrl(
      url.replace('/_matrix/media/v3/', '/_matrix/client/v1/media/'),
    )
  }
  if (url.includes('/_matrix/')) {
    return rewriteMatrixHomeserverUrl(url)
  }
  return url
}

function resolveMediaAttachmentUrl(
  _matrixClient: MatrixClient,
  attachment: IMessageAttachment,
): IMessageAttachment {
  const next: IMessageAttachment = {
    ...attachment,
    file: rewriteAttachmentMediaUrl(attachment.file),
  }
  if (attachment.thumbnail) {
    next.thumbnail = rewriteAttachmentMediaUrl(attachment.thumbnail)
  }
  return next
}

function enrichMessageMediaUrls(matrixClient: MatrixClient, message: IMessage): IMessage {
  if (!message.attachments?.length) {
    return message
  }

  return {
    ...message,
    attachments: message.attachments.map(attachment =>
      resolveMediaAttachmentUrl(matrixClient, attachment),
    ),
  }
}

export async function resolveMatrixSenderProfile(
  matrixClient: MatrixClient,
  mxid: string,
  room?: Room | null,
): Promise<MatrixSenderProfile> {
  const cached = profileCache.get(mxid)
  if (cached) {
    if (cached.avatarUrl || !mxid) {
      return cached
    }

    const backendAvatar = await resolveBackendAvatarCached(mxid)
    if (!backendAvatar) {
      return cached
    }

    const refreshed = {
      ...cached,
      avatarUrl: backendAvatar,
    }
    profileCache.set(mxid, refreshed)
    return refreshed
  }

  const pending = profilePending.get(mxid)
  if (pending) {
    return pending
  }

  const promise = (async (): Promise<MatrixSenderProfile> => {
    let displayName = mxid
    let mxcAvatar: string | null = null

    const member = room?.getMember(mxid)
    if (member) {
      displayName = member.name || member.rawDisplayName || displayName
      mxcAvatar = member.getMxcAvatarUrl?.() ?? null
    }

    if (displayName === mxid || !mxcAvatar) {
      try {
        const profile = await matrixClient.getProfileInfo(mxid)
        if (profile.displayname?.trim()) {
          displayName = profile.displayname.trim()
        }
        if (profile.avatar_url) {
          mxcAvatar = profile.avatar_url
        }
      } catch {
      }
    }

    const result: MatrixSenderProfile = {
      displayName: displayName === mxid || displayName.startsWith('@')
        ? formatMatrixDisplayName(mxid)
        : displayName,
      avatarUrl: resolveFallbackAvatarUrl(
        mxid,
        mxcToHttp(matrixClient, mxcAvatar),
        room?.roomId,
      ),
    }

    if (!result.avatarUrl && mxid) {
      const backendAvatar = await resolveBackendAvatarCached(mxid)
      if (backendAvatar) {
        result.avatarUrl = backendAvatar
      }
    }

    profileCache.set(mxid, result)
    return result
  })()

  profilePending.set(mxid, promise)

  try {
    return await promise
  } finally {
    profilePending.delete(mxid)
  }
}

function isMatrixAvatarCandidate(url: string | null | undefined): boolean {
  if (!url) {
    return false
  }

  return url.startsWith('mxc://')
    || url.startsWith('mxc:')
    || isMatrixMediaHttpUrl(url)
}

function resolveFallbackAvatarUrl(
  mxid: string,
  matrixAvatarUrl: string | null,
  _roomId?: string,
): string | null {
  // Список слева чаще даёт S3; Matrix mxc → blob часто ломается при смене чата.
  const viewAvatar = findViewAvatarByMatrixUserId(mxid)
  if (viewAvatar && !isMatrixAvatarCandidate(viewAvatar)) {
    return viewAvatar
  }

  return matrixAvatarUrl || viewAvatar
}

function applyProfileToMessage(message: IMessage, profile: MatrixSenderProfile): IMessage {
  const mxid = String(message.author_id || message.created_by?.id || message.author)
  const avatarUrl = resolveFallbackAvatarUrl(mxid, profile.avatarUrl)

  return {
    ...message,
    author_name: profile.displayName,
    avatar: avatarUrl ?? undefined,
    created_by: {
      ...message.created_by,
      id: mxid,
      full_name: profile.displayName,
      user_name: profile.displayName,
      username: mxid,
      avatar: avatarUrl ?? undefined,
    },
  }
}

export async function enrichMatrixMessages(
  matrixClient: MatrixClient,
  messages: IMessage[],
  room?: Room | null,
): Promise<IMessage[]> {
  const mxids = [...new Set(
    messages
      .map(message => String(message.author_id || message.created_by?.id || message.author))
      .filter(mxid => mxid.startsWith('@')),
  )]

  await Promise.all(
    mxids.map(mxid => resolveMatrixSenderProfile(matrixClient, mxid, room)),
  )

  return messages.map(message => {
    const messageWithMedia = enrichMessageMediaUrls(matrixClient, message)
    const mxid = String(messageWithMedia.author_id || messageWithMedia.created_by?.id || messageWithMedia.author)
    const profile = profileCache.get(mxid)
    if (!profile) {
      return messageWithMedia
    }

    return applyProfileToMessage(messageWithMedia, profile)
  })
}

export async function enrichMatrixMessage(
  matrixClient: MatrixClient,
  message: IMessage,
  room?: Room | null,
): Promise<IMessage> {
  const [enriched] = await enrichMatrixMessages(matrixClient, [message], room)
  return enriched
}
